import { useRequest, useSetState } from 'ahooks';
import { App, Button, Col, Form, Input, Row, Space, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import store from 'store2';

/**
 * APIs
 */
import { captcha, login } from '@/services/auth';

/**
 * Styles
 */
import styles from './styles.module.less';

const PageLogin: React.FC = () => {
    /**
     * Params
     */
    const [params] = useSearchParams();

    /**
     * Hooks
     */
    const { message } = App.useApp();

    const [form] = Form.useForm();

    /**
     * States
     */
    const [state, setState] = useSetState({
        captcha: '',
        key: '',
    });

    /**
     * Requests
     */
    const { loading: loadingCaptcha, run: runCaptcha } = useRequest(captcha, {
        onSuccess({ data: { code, data, msg } }) {
            if (code !== 0) {
                return message.error(msg);
            }

            // Handle nested response from auto-cargo-be (data.data contains actual captcha)
            const captchaData = (data as any)?.data ?? data;
            setState(captchaData);
        },
    });

    const { loading: loadingLogin, run: runLogin } = useRequest(login, {
        manual: true,
        onSuccess({ data: { code, data, msg } }) {
            if (code !== 0) {
                return message.error(msg);
            }

            // Handle nested response from auto-cargo-be
            const loginData = (data as any)?.data ?? data;
            store(loginData);

            window.location.href = params.get('redirect') || '/';
        },
    });

    /**
     * Events
     */
    const onSubmit = () => {
        form.validateFields()
            .then((value) => {
                runLogin({
                    ...value,
                    captcha: value.captcha.toLowerCase(),
                    key: state.key,
                });
            })
            .catch(() => {});
    };

    return (
        <Row className={styles.authLogin}>
            <Col span={12}>
                <Row className={styles.authLoginForm} justify="space-between">
                    <Col className="p-24">
                        <Space>
                            <img alt="" className={styles.authLoginLogo} src="/favicon.svg" />

                            <strong>Auto Cargo Admin</strong>
                        </Space>
                    </Col>

                    <Col className={styles.authLoginFormWrap}>
                        <Typography.Title className="mb-24" level={2}>
                            登录
                        </Typography.Title>

                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="username"
                                label="账号"
                                rules={[{ message: '请输入账号', required: true }]}
                            >
                                <Input size="large" placeholder="请输入账号" onPressEnter={onSubmit} />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label="密码"
                                rules={[{ message: '请输入密码', required: true }]}
                            >
                                <Input.Password size="large" placeholder="请输入密码" onPressEnter={onSubmit} />
                            </Form.Item>

                            <Form.Item
                                name="captcha"
                                label="验证码"
                                rules={[{ message: '请输入验证码', required: true }]}
                            >
                                <Row gutter={8}>
                                    <Col flex="auto">
                                        <Input size="large" onPressEnter={onSubmit} />
                                    </Col>

                                    <Col flex="none">
                                        <Spin spinning={loadingCaptcha}>
                                            <button
                                                type="button"
                                                aria-label="刷新验证码"
                                                onClick={runCaptcha}
                                                style={{
                                                    background: 'transparent',
                                                    border: 0,
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <img alt="captcha" height={40} src={state.captcha} width={100} />
                                            </button>
                                        </Spin>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Form>

                        <Button block loading={loadingLogin} size="large" type="primary" onClick={onSubmit}>
                            登录
                        </Button>
                    </Col>

                    <Col className="p-24">
                        Copyright &copy; {dayjs().format('YYYY')} Auto Cargo Enterprise Limited.
                    </Col>
                </Row>
            </Col>

            <Col span={12}>
                <img alt="" className={styles.authLoginImage} src="/auth/background.jpg" />
            </Col>
        </Row>
    );
};

export default PageLogin;
